import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Backdrop,
    CircularProgress,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Paper,
    Grid,
    IconButton,
} from "@mui/material";
import {
    NavigateNext,
    ChevronLeft,
    CheckCircle,
    Error as ErrorIcon,
    Info,
    Close,
} from "@mui/icons-material";
import TestMessageSteppers from "./TestMessageSteppers";
import TemplateSelectionStep from "./campaign/TemplateSelectionStep";
import TestMediaUpload from "./testMessage/TestMediaUpload";
import ParameterInsertionStep from "./testMessage/ParameterInsertionStep";
import PreviewStep from "./testMessage/PreviewStep";
import RecipientPhoneStep from "./testMessage/RecipientPhoneStep";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { analyzeTemplateParameters } from "../../utils/templateParameterUtils";
import { useLocation } from "react-router-dom";
import { WB_SX } from "../../ui/ux/uiTokens";

const TestMessage = () => {
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const notify = useNotification();

    // Config
    const [config, setConfig] = useState(null);
    const [phoneNumberId, setPhoneNumberId] = useState("");

    // Step 1: Template Selection
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateDetails, setTemplateDetails] = useState(null);
    const [loadingTemplateDetails, setLoadingTemplateDetails] = useState(false);

    // Step 2: Media Upload
    const [headerMediaId, setHeaderMediaId] = useState("");
    const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState("");
    const [headerMediaType, setHeaderMediaType] = useState(null);
    const [headerMediaLink, setHeaderMediaLink] = useState(null);

    // Step 3: Parameter Insertion
    const [parameters, setParameters] = useState(null);

    // Step 5: Recipients
    const [phoneNumbers, setPhoneNumbers] = useState([]);

    // Success/Error Messages
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Response Dialog State
    const [responseDialog, setResponseDialog] = useState({
        open: false,
        responseData: null,
    });

    // Fetch config on mount and check for template from navigation state
    useEffect(() => {
        fetchConfig();
        
        // Auto-select template if passed from navigation
        if (location.state?.template) {
            setSelectedTemplate(location.state.template);
        }
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business-config`
            );
            if (response.data) {
                setConfig(response.data);
                if (response.data.phone_number_id) {
                    setPhoneNumberId(response.data.phone_number_id);
                }
            }
        } catch (error) {
            console.log("No config found or error fetching config");
        }
    };

    useEffect(() => {
        if (selectedTemplate) {
            fetchTemplateDetails();
        } else {
            setTemplateDetails(null);
            setParameters(null);
        }
    }, [selectedTemplate]);

    useEffect(() => {
        // Store preview URL when media is uploaded
        // This will be handled in the MediaUploadStep component
    }, [headerMediaId]);

    const fetchTemplateDetails = async () => {
        const templateId = selectedTemplate?.template_id || selectedTemplate?.id;
        if (!templateId) {
            console.error("Template ID not found:", selectedTemplate);
            return;
        }

        setLoadingTemplateDetails(true);
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/template/${templateId}/details?fields=id,name,language,category,status,components,previous_category,parameter_format`
            );

            if (response.data?.error) {
                throw new Error(
                    response.data.error ||
                        response.data.message ||
                        "Failed to fetch template details"
                );
            }

            if (response.data && response.data.id) {
                setTemplateDetails(response.data);
            } else {
                throw new Error("Invalid response structure");
            }
        } catch (error) {
            console.error("Error fetching template details:", error);
            setTemplateDetails(null);
            notify(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    error.message ||
                    "Failed to fetch template details",
                "error"
            );
        } finally {
            setLoadingTemplateDetails(false);
        }
    };

    const handleNext = () => {
        if (currentStep === 0) {
            if (!selectedTemplate) {
                notify("Please select a template", "error");
                return;
            }
        } else if (currentStep === 1) {
            // Validate media upload if needed
            const headerComponent = templateDetails?.components?.find(
                (c) => c.type === "HEADER"
            );
            if (
                headerComponent &&
                ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format)
            ) {
                if (!headerMediaId) {
                    notify(
                        "Please upload media for the header before proceeding",
                        "error"
                    );
                    return;
                }
            }
        } else if (currentStep === 2) {
            // Validate parameters if needed using utility function
            const paramAnalysis = analyzeTemplateParameters(templateDetails);

            // Validate header parameters
            if (paramAnalysis.header.hasParameters) {
                const providedHeaderParams = (parameters?.header || []).filter(
                    (p) => p && p.trim()
                ).length;
                if (providedHeaderParams !== paramAnalysis.header.count) {
                    notify(
                        `Please fill all ${paramAnalysis.header.count} header parameter(s)`,
                        "error"
                    );
                    return;
                }
            }

            // Validate body parameters
            if (paramAnalysis.body.hasParameters) {
                const providedBodyParams = (parameters?.body || []).filter(
                    (p) => p && p.trim()
                ).length;
                if (providedBodyParams !== paramAnalysis.body.count) {
                    notify(
                        `Please fill all ${paramAnalysis.body.count} body parameter(s)`,
                        "error"
                    );
                    return;
                }
            }

            // Validate button parameters
            if (paramAnalysis.buttons.hasParameters && parameters?.buttons) {
                const buttonsData = parameters.buttons;
                Object.keys(buttonsData).forEach((buttonKey) => {
                    const buttonData = buttonsData[buttonKey];
                    if (buttonData && buttonData.parameters) {
                        const providedParams = buttonData.parameters.filter(
                            (p) => p && p.trim()
                        ).length;
                        const requiredParams = buttonData.parameters.length;
                        if (providedParams !== requiredParams) {
                            notify(
                                `Please fill all parameters for button: ${buttonData.buttonText}`,
                                "error"
                            );
                            return;
                        }
                    }
                });
            }
        } else if (currentStep === 3) {
            // Preview step - no validation needed, just proceed
        } else if (currentStep === 4) {
            // Validate recipients
            if (phoneNumbers.length === 0) {
                notify("Please add at least one recipient phone number", "error");
                return;
            }
            handleSendTestMessages();
            return;
        }
        setCurrentStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setCurrentStep((prev) => prev - 1);
    };

    const buildComponents = () => {
        const components = [];
        const headerComponent = templateDetails?.components?.find(
            (c) => c.type === "HEADER"
        );
        const bodyComponent = templateDetails?.components?.find(
            (c) => c.type === "BODY"
        );

        // Header component
        if (headerComponent) {
            if (headerComponent.format === "TEXT") {
                const headerParamCount =
                    (headerComponent.text?.match(/\{\{(\d+)\}\}/g) || []).length;
                if (headerParamCount > 0 && parameters?.header) {
                    components.push({
                        type: "HEADER",
                        parameters: parameters.header
                            .filter((p) => p && p.trim())
                            .map((text) => ({
                                type: "text",
                                text: String(text).trim(),
                            })),
                    });
                }
            } else if (
                ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format)
            ) {
                if (headerMediaId) {
                    components.push({
                        type: "HEADER",
                        parameters: [
                            {
                                type: headerComponent.format.toLowerCase(),
                                [headerComponent.format.toLowerCase()]: {
                                    id: headerMediaId,
                                },
                            },
                        ],
                    });
                }
            }
        }

        // Body component
        if (bodyComponent && parameters?.body) {
            const bodyParamCount =
                (bodyComponent.text?.match(/\{\{(\d+)\}\}/g) || []).length;
            if (bodyParamCount > 0) {
                components.push({
                    type: "BODY",
                    parameters: parameters.body
                        .filter((p) => p && p.trim())
                        .map((text) => ({
                            type: "text",
                            text: String(text).trim(),
                        })),
                });
            }
        }

        return components;
    };

    const handleSendTestMessages = async () => {
        setLoading(true);
        const results = {
            success: [],
            errors: [],
        };

        try {
            const components = buildComponents();

            // Get template_id from selected template
            const templateId = selectedTemplate.template_id || selectedTemplate.id;

            // Build payload with template_id, components, recipients array, and media info
            const payload = {
                template_id: templateId,
                components: components,
                recipients: phoneNumbers, // Array of phone numbers with country code
                media_type: headerMediaType || null,
                media_link: headerMediaLink || null,
            };

            // Phone Number ID can be optional if stored in config
            const phoneNumberIdToUse = phoneNumberId.trim() || (config?.phone_number_id || "");

            // Use endpoint with phone number ID if available, otherwise backend will use config
            const endpoint = phoneNumberIdToUse
                ? `${base_url}/whatsapp-business/phone-number/${phoneNumberIdToUse}/send-template`
                : `${base_url}/whatsapp-business/phone-number/send-template`;

            const response = await axiosInstance.post(endpoint, payload);

            // Handle new API response structure
            if (response.data?.success) {
                // Success response
                const responseData = response.data.data || response.data;
                
                // Show detailed response dialog
                setResponseDialog({
                    open: true,
                    responseData: {
                        success: true,
                        message: response.data.message || "Messages queued successfully",
                        template_id: responseData.template_id,
                        template_name: responseData.template_name,
                        total_recipients: responseData.total_recipients || phoneNumbers.length,
                        total_inserted: responseData.total_inserted || 0,
                        failed: responseData.failed || 0,
                        campaign_name: responseData.campaign_name,
                        errors: responseData.errors || [],
                    },
                });

                // Show success notification
                notify(
                    response.data.message || "Messages queued successfully",
                    "success"
                );

                // Reset form after successful sends
                setTimeout(() => {
                    resetForm();
                }, 2000);
            } else {
                // Error response
                const errorData = response.data;
                setResponseDialog({
                    open: true,
                    responseData: {
                        success: false,
                        error: errorData.error || "Failed to queue messages",
                        message: errorData.message || "An error occurred",
                        total_inserted: errorData.total_inserted || 0,
                        failed: errorData.failed || phoneNumbers.length,
                        errors: errorData.errors || [],
                        validation_errors: errorData.errors || {}, // For validation errors
                    },
                });

                notify(
                    errorData.error || errorData.message || "Failed to send messages",
                    "error"
                );
            }
        } catch (error) {
            console.error("Error sending test messages:", error);
            let errorMessage = "Failed to send messages";
            let errorDetails = null;

            if (error.response?.data) {
                const errorData = error.response.data;
                
                // Extract error message
                if (errorData.data?.error?.error_user_msg) {
                    errorMessage = errorData.data.error.error_user_msg;
                } else if (errorData.data?.error?.message) {
                    errorMessage = errorData.data.error.message;
                } else if (errorData.error) {
                    errorMessage =
                        typeof errorData.error === "string"
                            ? errorData.error
                            : errorData.error.message ||
                              errorData.error.error_user_msg ||
                              errorMessage;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                }

                // Build error response data
                errorDetails = {
                    success: false,
                    error: errorData.error || errorMessage,
                    message: errorData.message || "An error occurred",
                    total_inserted: errorData.total_inserted || 0,
                    failed: errorData.failed || phoneNumbers.length,
                    errors: errorData.errors || [],
                    validation_errors: errorData.errors || {}, // For validation errors (422)
                };
            } else {
                errorDetails = {
                    success: false,
                    error: errorMessage,
                    message: "Network error or server unavailable",
                    total_inserted: 0,
                    failed: phoneNumbers.length,
                    errors: [],
                };
            }

            // Show error dialog
            setResponseDialog({
                open: true,
                responseData: errorDetails,
            });

            notify(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setCurrentStep(0);
        setSelectedTemplate(null);
        setTemplateDetails(null);
        setHeaderMediaId("");
        setHeaderMediaPreviewUrl("");
        setHeaderMediaType(null);
        setHeaderMediaLink(null);
        setParameters(null);
        setPhoneNumbers([]);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleCloseResponseDialog = () => {
        setResponseDialog({ open: false, responseData: null });
    };

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflowX: "hidden",
                ...WB_SX.page,
            }}
        >
            <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, mb: 2 }}>
                <TestMessageSteppers currentStep={currentStep} />
            </Box>

            <Box
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    overflowX: "hidden",
                }}
            >
                {currentStep === 0 && (
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                        <TemplateSelectionStep
                            onTemplateSelect={setSelectedTemplate}
                            selectedTemplate={selectedTemplate}
                        />
                    </Box>
                )}

                {currentStep === 1 && (
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                        <TestMediaUpload
                            selectedTemplate={selectedTemplate}
                            templateDetails={templateDetails}
                            loadingTemplateDetails={loadingTemplateDetails}
                            onMediaUploaded={(mediaInfo) => {
                                if (typeof mediaInfo === 'object' && mediaInfo !== null) {
                                    setHeaderMediaId(mediaInfo.id || "");
                                    setHeaderMediaType(mediaInfo.media_type || null);
                                    setHeaderMediaLink(mediaInfo.media_link || null);
                                } else {
                                    // Backward compatibility: if it's just a string/ID
                                    setHeaderMediaId(mediaInfo || "");
                                    setHeaderMediaType(null);
                                    setHeaderMediaLink(null);
                                }
                            }}
                            onPreviewUrlChange={setHeaderMediaPreviewUrl}
                        />
                    </Box>
                )}

                {currentStep === 2 && (
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                        <ParameterInsertionStep
                            selectedTemplate={selectedTemplate}
                            templateDetails={templateDetails}
                            loadingTemplateDetails={loadingTemplateDetails}
                            parameters={parameters}
                            onParametersChange={setParameters}
                        />
                    </Box>
                )}

                {currentStep === 3 && (
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                        <PreviewStep
                            selectedTemplate={selectedTemplate}
                            templateDetails={templateDetails}
                            loadingTemplateDetails={loadingTemplateDetails}
                            parameters={parameters}
                            headerMediaId={headerMediaId}
                            headerMediaPreviewUrl={headerMediaPreviewUrl}
                        />
                    </Box>
                )}

                {currentStep === 4 && (
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                        <RecipientPhoneStep
                            phoneNumbers={phoneNumbers}
                            onPhoneNumbersChange={setPhoneNumbers}
                        />
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                    mt: 3,
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                }}
            >
                <Button
                    disabled={currentStep === 0}
                    onClick={handleBack}
                    startIcon={<ChevronLeft />}
                    sx={WB_SX.secondaryButton}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<NavigateNext />}
                    disabled={loading}
                    sx={WB_SX.primaryButton}
                >
                    {currentStep === 4 ? "Send Test Messages" : "Next"}
                </Button>
            </Box>

            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Response Details Dialog */}
            <Dialog
                open={responseDialog.open}
                onClose={handleCloseResponseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        bgcolor: responseDialog.responseData?.success
                            ? "success.main"
                            : "error.main",
                        color: "white",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {responseDialog.responseData?.success ? (
                            <CheckCircle />
                        ) : (
                            <ErrorIcon />
                        )}
                        <Typography variant="h6">
                            {responseDialog.responseData?.success
                                ? "Messages Queued Successfully"
                                : "Failed to Queue Messages"}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleCloseResponseDialog}
                        sx={{ color: "white" }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ mt: 2 }}>
                    {responseDialog.responseData && (
                        <Box>
                            {/* Main Message */}
                            <Alert
                                severity={
                                    responseDialog.responseData.success
                                        ? "success"
                                        : "error"
                                }
                                sx={{ mb: 3 }}
                                icon={
                                    responseDialog.responseData.success ? (
                                        <CheckCircle />
                                    ) : (
                                        <ErrorIcon />
                                    )
                                }
                            >
                                <Typography variant="body1" fontWeight="bold">
                                    {responseDialog.responseData.message ||
                                        responseDialog.responseData.error}
                                </Typography>
                            </Alert>

                            {/* Success Response Details */}
                            {responseDialog.responseData.success && (
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Template Name
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold">
                                                {responseDialog.responseData.template_name ||
                                                    "N/A"}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Template ID
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily: "monospace" }}
                                            >
                                                {responseDialog.responseData.template_id || "N/A"}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Total Recipients
                                            </Typography>
                                            <Typography variant="h6" color="primary">
                                                {responseDialog.responseData.total_recipients || 0}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, textAlign: "center", bgcolor: "success.light" }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Successfully Queued
                                            </Typography>
                                            <Typography variant="h6" color="success.dark">
                                                {responseDialog.responseData.total_inserted || 0}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                textAlign: "center",
                                                bgcolor:
                                                    responseDialog.responseData.failed > 0
                                                        ? "error.light"
                                                        : "transparent",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Failed
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                color={
                                                    responseDialog.responseData.failed > 0
                                                        ? "error.dark"
                                                        : "text.primary"
                                                }
                                            >
                                                {responseDialog.responseData.failed || 0}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    {responseDialog.responseData.campaign_name && (
                                        <Grid item xs={12}>
                                            <Paper variant="outlined" sx={{ p: 2 }}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Campaign Name
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {responseDialog.responseData.campaign_name}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            )}

                            {/* Error Response Details */}
                            {!responseDialog.responseData.success && (
                                <Box>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Paper
                                                variant="outlined"
                                                sx={{ p: 2, textAlign: "center" }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Successfully Queued
                                                </Typography>
                                                <Typography variant="h6" color="success.main">
                                                    {responseDialog.responseData.total_inserted || 0}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    textAlign: "center",
                                                    bgcolor: "error.light",
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Failed
                                                </Typography>
                                                <Typography variant="h6" color="error.dark">
                                                    {responseDialog.responseData.failed || 0}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Errors List */}
                            {responseDialog.responseData.errors &&
                                Array.isArray(responseDialog.responseData.errors) &&
                                responseDialog.responseData.errors.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Error Details:
                                        </Typography>
                                        <List dense>
                                            {responseDialog.responseData.errors.map(
                                                (errorItem, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                            <ErrorIcon color="error" fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                typeof errorItem === "string"
                                                                    ? errorItem
                                                                    : errorItem.message ||
                                                                      errorItem.error ||
                                                                      JSON.stringify(errorItem)
                                                            }
                                                            secondary={
                                                                errorItem.phone_number ||
                                                                errorItem.recipient ||
                                                                errorItem.field
                                                            }
                                                        />
                                                    </ListItem>
                                                )
                                            )}
                                        </List>
                                    </Box>
                                )}

                            {/* Validation Errors */}
                            {responseDialog.responseData.validation_errors &&
                                typeof responseDialog.responseData.validation_errors === "object" &&
                                !Array.isArray(responseDialog.responseData.validation_errors) &&
                                Object.keys(responseDialog.responseData.validation_errors).length >
                                    0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Validation Errors:
                                        </Typography>
                                        <List dense>
                                            {Object.entries(
                                                responseDialog.responseData.validation_errors
                                            ).map(([field, messages]) => (
                                                <ListItem key={field}>
                                                    <ListItemIcon>
                                                        <ErrorIcon color="error" fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={field}
                                                        secondary={
                                                            Array.isArray(messages)
                                                                ? messages.join(", ")
                                                                : String(messages)
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handleCloseResponseDialog}
                        variant="contained"
                        color={responseDialog.responseData?.success ? "success" : "error"}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TestMessage;

